import React, { useState, useEffect } from 'react';
import { useIMask } from 'react-imask';
import { InputTitleEnum } from '../InputTitleEnum/InputTitleEnum';
import { isFinite, toInteger, toString } from 'lodash';
import { EditActions } from '../../../components/_common/EditLoadErrors/EditLoadErrors';
import { InputGroup, Form } from 'react-bootstrap';
import './Textfield.scss';
import {NumericFormat as NumberFormat} from 'react-number-format';

function toImaskOptions(mask: string | (string | RegExp)[]): { mask: string; _maxLength?: number; _charRegex?: RegExp } {
    if (typeof mask === 'string') {
        return { mask: mask.replace(/9/g, '0') };
    }
    if (Array.isArray(mask) && mask.length > 0) {
        const first = mask[0];
        if (first instanceof RegExp) {
            return {
                mask: '',
                _maxLength: mask.length,
                _charRegex: first,
            };
        }
        return { mask: '' };
    }
    return { mask: '0' };
}

interface CommonPropsType {
    disabled?: boolean;
    onBlur?: () => void;
    onChange?: (event: any) => void;
}

interface MaskedTextfieldProps {
    mask: string | (string | RegExp)[];
    inputValue: string;
    setInputValue: (v: string) => void;
    commonProps: CommonPropsType;
    allow_underscore?: boolean;
    name?: string;
    label: string;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    maxLength?: number;
    endIcon?: React.ReactElement;
    textfieldErrors: React.ReactNode;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    validation?: boolean;
    onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
}

function MaskedTextfield({
    mask,
    inputValue,
    setInputValue,
    commonProps,
    allow_underscore,
    name,
    label,
    required,
    error,
    disabled,
    maxLength,
    endIcon,
    textfieldErrors,
    onChange,
    validation,
    onPaste,
}: MaskedTextfieldProps): React.ReactElement {
    const { _maxLength, _charRegex, ...opts } = toImaskOptions(mask);
    const { ref: imaskRef, setValue } = useIMask(
        { ...opts, onAccept: (value: string) => {
            const clean = allow_underscore ? value : value.replace(/_+/g, '');
            setInputValue(clean);
            
            const inputElement = imaskRef.current || null;  
            const syntheticEvent = {
                target: { 
                    value: clean, 
                    name: name || '',
                },
                currentTarget: inputElement,
            } as React.ChangeEvent<HTMLInputElement>;
            
            if (onChange) {
                onChange(syntheticEvent);
            }
            
            if (commonProps.onChange) {
                commonProps.onChange(syntheticEvent);
            }
        },
        onBlur: () => commonProps.onBlur?.(),
    });

    useEffect(() => {
        const valueToSet = (inputValue != null && inputValue !== 'undefined') ? String(inputValue) : '';
        if (typeof opts.mask === 'string' && opts.mask === '') {
            if (imaskRef.current) {
                (imaskRef.current as HTMLInputElement).value = valueToSet;
            }
        } else {
            setValue(valueToSet);
        }
    }, [inputValue, setValue, opts.mask]);

    return (
        <div className="Textfield-FormContainer">
            <form onSubmit={(e) => e.preventDefault()}>
                <InputGroup className="Textfield-Validation">
                    <Form.Label>
                        <InputGroup.Text
                            className={error ? 'Textfield-InputTitle Textfield-Error' : 'Textfield-InputTitle'}
                        >
                            {required ? `*${label}` : label}
                        </InputGroup.Text>
                    </Form.Label>
                    <div className="Textfield-InputRow">
                        <Form.Control
                            ref={imaskRef as React.Ref<HTMLInputElement>}
                            className={error ? 'Textfield-InputBox InputBox-Error' : 'Textfield-InputBox'}
                            type="text"
                            name={name}
                            {...(typeof opts.mask === 'string' && opts.mask === '' ? { value: inputValue || '' } : {})}
                            disabled={disabled}
                            maxLength={_maxLength || maxLength}
                            onPaste={onPaste}
                            onChange={(e) => {
                                let val = e.target.value;
                                // Filter invalid characters if a per-character regex is defined
                                if (_charRegex) {
                                    val = val.split('').filter(ch => _charRegex.test(ch)).join('');
                                }
                                const clean = allow_underscore ? val : val.replace(/_+/g, '');
                                setInputValue(clean);
                                
                                const syntheticEvent = {
                                    target: { 
                                        value: clean, 
                                        name: name || '',
                                    },
                                    currentTarget: e.currentTarget,
                                } as React.ChangeEvent<HTMLInputElement>;
                                
                                if (onChange) {
                                    onChange(syntheticEvent);
                                }
                                
                                if (commonProps.onChange) {
                                    commonProps.onChange(syntheticEvent);
                                }
                            }}
                        />
                        {endIcon && <InputGroup.Text className="Textfield-Icon">{endIcon}</InputGroup.Text>}
                    </div>
                    {textfieldErrors}
                </InputGroup>
            </form>
        </div>
    );
}

interface TextfieldProps {
    label: string;
    helperText?: string;
    select?: boolean;
    required?: boolean;
    type?: string;
    min?: string;
    max?: string;
    step?: string;
    maxLength?: number;
    multiline?: boolean;
    rows?: number;
    error?: boolean;
    id?: string;
    mask?: string | (string | RegExp)[];
    name?: string;
    onChange?:
        | ((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void)
        | undefined;
    value?: string | number | null;
    inputTitle?: string | null;
    onBlur?: (value: string | number | null | undefined) => void;
    onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
    disabled?: boolean;
    inputProps?: () => void;
    endIcon?: React.ReactElement;
    thousandSeparator?: boolean;
    allow_underscore?: boolean;
}

export const Textfield = ({
    label,
    name,
    onChange,
    required,
    mask,
    error,
    value,
    onBlur,
    inputTitle,
    disabled,
    helperText,
    endIcon,
    thousandSeparator,
    maxLength,
    allow_underscore,
    onPaste,
}: TextfieldProps): React.ReactElement => {
    const [inputValue, setInputValue] = useState(toString(value));
    let validation = false;
    let errorMessage = '';
    if (inputTitle === InputTitleEnum.STAGE_TIME) {
        if (value !== null) {
            validation = value && toInteger(value) > 0 && toInteger(value) < 999 ? false : true;
            errorMessage = EditActions.STAGE_TIME_ERROR;
        }
    }
    if (inputTitle === InputTitleEnum.MILES_FROM_WELL) {
        if ((value && isFinite(value)) || value === null || value === '') {
            validation = false;
            errorMessage = '';
        } else {
            validation = value && toInteger(value) > 0 ? false : true;
            errorMessage = EditActions.MILES_FROM_WELL_ERROR;
        }
    }

    const commonProps: CommonPropsType = {
        onBlur: (): void => {
            if (onBlur) {
                onBlur(inputValue);
            }
        },
        onChange: (event: any): void => {
            const cleanValue = mask ?
                (allow_underscore ? event.target.value : event.target.value.replace(/_+/, '')) :
                event.target.value;
            setInputValue(cleanValue);
            if (!validation && onChange) {
                onChange(event);
            }
        },
    };

    if (!validation) {
        commonProps.disabled = disabled;
    }

    useEffect(() => {
        setInputValue(toString(value));
    }, [value]);
    const textfieldErrors =
        error || validation ? (
            <div className="Textfield-Invalid">
                <Form.Control.Feedback type="invalid" aria-invalid="false">
                    {helperText || errorMessage || 'This field is required'}
                </Form.Control.Feedback>
            </div>
        ) : (
            <div className="Textfield-valid">
                <Form.Control.Feedback type="valid" aria-invalid="true">
                    {error}
                    {helperText}
                </Form.Control.Feedback>
            </div>
        );

    const componentProps = !mask ? commonProps : {};
    const returnComponent = (
        <div className="Textfield-FormContainer">
            <form onSubmit={e => e.preventDefault()}>
                <InputGroup className="Textfield-Validation">
                    <Form.Label>
                        {/* <InputGroup.Prepend> */}
                            <InputGroup.Text
                                className={error ? 'Textfield-InputTitle Textfield-Error' : 'Textfield-InputTitle'}
                            >
                                {required ? `*${label}` : label}
                            </InputGroup.Text>
                        {/* </InputGroup.Prepend> */}
                    </Form.Label>
                    <div className="Textfield-InputRow">
                        <Form.Control
                            className={error ? 'Textfield-InputBox InputBox-Error' : 'Textfield-InputBox'}
                            type="text"
                            name={name}
                            value={inputValue ? inputValue : ''}
                            disabled={disabled}
                            maxLength={maxLength}
                            {...componentProps}
                        />
                        {endIcon && (
                            // <InputGroup.Append>
                                <InputGroup.Text className={'Textfield-Icon'}>{endIcon}</InputGroup.Text>
                            // </InputGroup.Append>
                        )}
                    </div>
                    {textfieldErrors}
                </InputGroup>
            </form>
        </div>
    );

    const returnThousandSeparatorComponent = (
        <div className="Textfield-FormContainer">
            <InputGroup className="Textfield-Validation">
                <Form.Label>
                    {/* <InputGroup.Prepend> */}
                        <InputGroup.Text
                            className={error ? 'Textfield-InputTitle Textfield-Error' : 'Textfield-InputTitle'}
                        >
                            {required ? `*${label}` : label}
                        </InputGroup.Text>
                    {/* </InputGroup.Prepend> */}
                </Form.Label>
                <div className="Textfield-InputRow">
                    <NumberFormat
                        className={
                            disabled
                                ? 'Textfield-InputBox2 Textfield-Disabled'
                                : error
                                ? 'Textfield-InputBox2 InputBox-Error2'
                                : 'Textfield-InputBox2'
                        }
                        onChange={(event: any): void => {
                            const cleanValue = event.target.value.replace(/,/g, '');
                            setInputValue(cleanValue);
                            if (onChange) {
                                onChange(event.target.value.replace(/,/g, ''));
                            }
                        }}
                        defaultValue={inputValue || ''}
                        thousandSeparator
                        maxLength={maxLength}
                        disabled={disabled}
                    />
                </div>
                {textfieldErrors}
            </InputGroup>
        </div>
    );

    if (thousandSeparator) {
        return returnThousandSeparatorComponent;
    }
    if (mask) {
        return (
            <MaskedTextfield
                mask={mask}
                inputValue={inputValue}
                setInputValue={setInputValue}
                commonProps={commonProps}
                allow_underscore={allow_underscore}
                name={name}
                label={label}
                required={required}
                error={error}
                disabled={disabled}
                maxLength={maxLength}
                endIcon={endIcon}
                textfieldErrors={textfieldErrors}
                onChange={onChange}
                validation={validation}
                onPaste={onPaste}
            />
        );
    }
    return returnComponent;
};

export default Textfield;
